import React, { useState } from "react";
import * as Icons from "lucide-react";
import { services, sectors, included } from "../../mock";

export const Eyebrow = ({ children }) => (
  <div className="text-yellow-400 text-[13px] tracking-[0.25em] uppercase font-medium">{children}</div>
);

export const SectionTitle = ({ children }) => (
  <h2 className="font-display font-bold text-white text-[40px] sm:text-[54px] tracking-[-0.03em] leading-tight mt-3 relative inline-block">
    {children}
    <span className="block w-16 h-[3px] bg-yellow-400 mt-1" />
  </h2>
);

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Square;
  return <C {...p} />;
};

export function Services() {
  const [open, setOpen] = useState(null);
  return (
    <section id="services" className="bg-[#141416] py-28">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-[13.3%]">
        <div className="reveal grid lg:grid-cols-2 gap-8 items-end">
          <div><Eyebrow>Expertise</Eyebrow><SectionTitle>Our Services</SectionTitle></div>
          <p className="text-white/60 leading-relaxed max-w-[520px] lg:justify-self-end">
            Full-spectrum construction, from first sketch to final walkthrough. We tailor our approach to each project across commercial, residential and institutional work.
          </p>
        </div>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div
              key={s.title}
              data-testid={`service-card-${i}`}
              onClick={() => setOpen(open === i ? null : i)}
              className="reveal group relative cursor-pointer rounded-3xl bg-zinc-900 border border-white/10 p-7 flex flex-col min-h-[340px] hover:border-yellow-400/60 hover:-translate-y-1.5 hover:shadow-[0_20px_60px_-20px_rgba(250,204,21,0.25)] transition-[transform,border-color,box-shadow] duration-300"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="w-14 h-14 rounded-2xl bg-yellow-400/10 flex items-center justify-center group-hover:bg-yellow-400 transition-colors duration-300">
                <Icon name={s.icon} className="w-7 h-7 text-yellow-400 group-hover:text-black transition-colors duration-300" />
              </div>
              <h3 className="text-white text-[21px] font-bold mt-7 leading-snug">{s.title}</h3>
              <p className="text-white/55 text-[15px] leading-relaxed mt-3">{s.text}</p>
              <div className={`grid transition-[grid-template-rows] duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <ul className="overflow-hidden">
                  {s.details.map((d) => (
                    <li key={d} className="flex items-center gap-2 text-white/70 text-sm pt-2"><Icons.Check className="w-3.5 h-3.5 text-yellow-400" />{d}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto pt-6 flex items-center justify-between border-t border-white/10">
                <span className="font-mono-x text-yellow-400 text-[12px] tracking-wider">{s.phase}</span>
                <Icons.Plus className={`w-4 h-4 text-white/50 transition-transform duration-300 ${open === i ? "rotate-45 text-yellow-400" : ""}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Sectors() {
  return (
    <section id="sectors" className="bg-black py-28">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-[13.3%]">
        <div className="reveal grid lg:grid-cols-2 gap-8 items-end">
          <div><Eyebrow>Where we build</Eyebrow><SectionTitle>Sectors We Serve</SectionTitle></div>
          <p className="text-white/60 leading-relaxed max-w-[450px] lg:justify-self-end">
            Dependable construction solutions for a wide range of industries throughout Virginia.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {sectors.map((s, i) => (
            <div
              key={s.title}
              data-testid={`sector-card-${i}`}
              className="reveal group rounded-3xl bg-zinc-900 border border-white/10 min-h-[200px] p-6 flex flex-col items-center text-center hover:bg-zinc-800 hover:border-yellow-400/50 hover:-translate-y-1 transition-[transform,background-color,border-color] duration-300"
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              <Icon name={s.icon} className="w-12 h-12 text-yellow-400 mt-3 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-white font-bold text-[19px] mt-5 leading-snug">{s.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Included() {
  return (
    <section className="bg-black pb-24 pt-4">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-[13.3%]">
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-2 bg-yellow-400/10 text-yellow-400 rounded-full px-6 py-3 text-[14px]">
            <Icons.Gift className="w-4 h-4" /> INCLUDED WITH EVERY PROJECT
          </span>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {included.map((c, i) => (
            <div key={c.title} className="reveal rounded-3xl bg-zinc-900 border border-white/10 p-7 hover:border-white/25 hover:-translate-y-1 transition-[transform,border-color] duration-300" style={{ transitionDelay: `${i * 60}ms` }}>
              <Icon name={c.icon} className="w-9 h-9 text-yellow-400" strokeWidth={1.6} />
              <h3 className="text-white font-bold text-[19px] mt-7">{c.title}</h3>
              <p className="text-white/55 text-[15px] leading-relaxed mt-3">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
